import { useSelectorEq } from "commons/store/common";
import { useDispatch } from "react-redux";
import { type IState } from "store/modules";
import {
  ActiveType,
  type CalculateActiveTab,
  type CalculateContentSubTab,
  type CalculateMediaSubTab,
  type CalculatePartnersSubTab,
} from "./CalculateVo";
import {
  apiGcalculateTotalList,
  apiGdownloadExcel,
  apiGexpectDataList,
  apiGsettlementDataList,
  apiPMsnExcelSave,
  apiPcalculatePublish,
  apiPcalculateViewListSave,
  apiPpublishMediaExcel,
  rdxResetActiveTab,
  rdxResetCalculateGridData,
  rdxResetExpectListData,
  rdxResetSettlementExcelViewList,
  rdxResetSettlementListData,
  rdxSetActiveTab,
  rdxSetCalculateTaxRowUpdate,
  rdxSetSaveFlag,
  rdxSetSearchMonthTab,
  rdxSetSettlementExcelViewList,
  rdxSetSubTab,
  rdxSetViewListFilter,
} from "./CalculateR";
import { useApi } from "store/modules/common";
import { useEffect } from "react";
import { useAlert, useIConPopup } from "components/popup/popupHook";
import useLoading from "commons/loading/store/loadingHook";
import { useChannelListLoad } from "pages/statistics/store/StatisticsHook";
import type { StatisticsTabType } from "pages/statistics/store/StatisticsVo";
import * as XLSX from "xlsx";
import { useSetSelectBox } from "commons/ui/useUihook";

/**
 * @function useNowYearSelectBox 년도선택 셀렉트박스
 * @param init 시작할 년도(데이터 있는 년도부터, 없으면 현재 년도)
 * @returns year: 현재년도, yearData: 셀렉트박스 년도 리스트
 */
export function useNowYearSelectBox(init?: number) {
  const now = new Date();
  const year = now.getFullYear();
  const yearData = [];
  const initYear = init ? init : year;
  for (let i = initYear; i < year + 1; i++) {
    yearData.push({ id: `${i}`, text: `${i}년`, value: `${i}` });
  }

  return { year, yearData };
}

/**
 * @function useMonthList 1~12월 탭 리스트 리턴해주는 함수
 * @param viewMonth viewMonth 이후는 disable 처리 (데이터 볼수없게끔 처리)
 * @returns 1~12월 탭 데이터 리스트
 */
export function useMonthList(viewMonth?: number) {
  const monthData = [];
  for (let i = 1; i <= 12; i++) {
    monthData.push({
      id: String(i),
      text: i < 10 ? `0${i}월` : `${i}월`,
      disable: viewMonth ? (i <= viewMonth ? false : true) : false,
    });
  }
  return monthData;
}
/**
 * 콘텐츠 탭 리스트 API 호출
 * @returns 콘텐츠 탭 리스트 조회 API 호출
 */
export const useContentsCalculateList = (tab: string) => {
  const { apiResult } = useApi();

  const loadList = async (page?: number) => {
    try {
      const param = page !== undefined ? page : 0;
      await apiResult(apiGcalculateTotalList, { tab: tab, page: param });
    } catch (error) {
      throw new Error("API Error: apiGcalculateTotalList");
    }
  };

  return { loadList };
};

/**
 * 예상수익 리스트 API 호출
 * @returns 예상수익 리스트 조회 API 호출
 */
export const useExpectList = () => {
  const { apiResult } = useApi();

  const loadList = async (page?: number) => {
    try {
      const param = page !== undefined ? page : 0;
      await apiResult(apiGexpectDataList, { page: param });
    } catch (error) {
      throw new Error("API Error: apiGexpectDataList");
    }
  };

  return { loadList };
};

/**
 * 정산금 리스트 API 호출
 * @returns 정산금 리스트 조회 API 호출
 */
export const useSettlementList = () => {
  const { apiResult } = useApi();

  const loadList = async (page?: number) => {
    try {
      const param = page !== undefined ? page : 0;
      await apiResult(apiGsettlementDataList, { page: param });
    } catch (error) {
      throw new Error("API Error: apiGsettlementDataList");
    }
  };

  return { loadList };
};

/**
 * 정산관리 > 대분류 액티브 탭
 */
export const useCalculateTabAction = () => {
  const { tab } = useSelectorEq((state: IState) => ({
    tab: state.calculate.activeTab,
  }));

  const dispatch = useDispatch();
  const taxFilter = useSetSelectBox("calculate_tax_filter");
  const patFilter = useSetSelectBox("calculate_pay_filter");
  const approvalFilter = useSetSelectBox("calculate_approval_filter");

  const tabChange = (v: string) => {
    dispatch(rdxSetActiveTab(v as CalculateActiveTab));
    taxFilter.changeValue(undefined!);
    patFilter.changeValue(undefined!);
    approvalFilter.changeValue(undefined!);
  };

  useEffect(() => {
    return () => {
      dispatch(rdxResetActiveTab());
    };
  }, []);
  return { tab, tabChange };
};

/**
 * 정산관리 > 소분류 액티브 탭
 */
export const useSubTabAction = (tab: string) => {
  const { subTab } = useSelectorEq((state: IState) => ({
    subTab: state.calculate.subTab[tab],
  }));

  const dispatch = useDispatch();
  const taxFilter = useSetSelectBox("calculate_tax_filter");
  const patFilter = useSetSelectBox("calculate_pay_filter");
  const approvalFilter = useSetSelectBox("calculate_approval_filter");

  const tabChange = (v: string) => {
    dispatch(rdxSetSubTab(v as CalculateContentSubTab | CalculateMediaSubTab | CalculatePartnersSubTab));
    taxFilter.changeValue(undefined!);
    patFilter.changeValue(undefined!);
    approvalFilter.changeValue(undefined!);
  };

  // useEffect(() => {
  //   return () => {
  //     dispatch(rdxResetSubTab());
  //   };
  // }, []);

  return { subTab, tabChange };
};

export const useSelectorSearchDate = (tab: string, subTab: string) => {
  const { year, searchMonth } = useSelectorEq((state: IState) => ({
    year: state.ui.selectbox?.[`calculate_${tab}_${subTab}_search_year`],
    searchMonth: state.calculate.searchMonth && state.calculate.searchMonth[tab],
  }));
  return { year, searchMonth };
};

export const useSearchDateAction = (tab: string, subTab: string) => {
  const dispatch = useDispatch();
  const { year, searchMonth } = useSelectorSearchDate(tab, subTab);
  const change = async (origin: string, val: string) => {
    if (origin !== val) {
      dispatch(rdxSetSearchMonthTab(Number(val)));
      dispatch(rdxResetSettlementExcelViewList());
    }
  };

  const changeSelectBox = async (month: number) => {
    dispatch(rdxSetSearchMonthTab(Number(month)));
    dispatch(rdxResetSettlementExcelViewList());
  };

  return { year, searchMonth, change, changeSelectBox };
};

export const useOptionListInit = (tab: string) => {
  const { subTab, optionList, selectChannel } = useSelectorEq((state: IState) => ({
    subTab: state.calculate.subTab,
    optionList: state.statistics.channelList,
    selectChannel: state.calculate.selectChannelList,
  }));

  const channelListLoad = useChannelListLoad();
  useEffect(() => {
    channelListLoad(tab as StatisticsTabType, subTab[tab] === "pp" ? "SETTLEMENT" : undefined);
  }, [tab, subTab]);
  return { optionList, selectChannel };
};

export const useContentsTabListLoad = (subTab: string) => {
  const { viewList, total } = useSelectorEq((state: IState) => ({
    viewList: state.calculate.contents[subTab].viewList,
    total: state.calculate.contents[subTab].total,
  }));
  return { viewList, total };
};

/**
 * 콘텐츠 탭 init API
 * @param tab 대분류 탭
 * @param subTab 소분류 탭
 * @returns
 */
export const useContentsTabListInit = (tab: string, subTab: string) => {
  const dispatch = useDispatch();
  const { year, searchMonth } = useSelectorSearchDate(tab, subTab);
  const { selectChannel, taxFilter, payFilter, approvalFilter } = useSelectorEq((state: IState) => ({
    selectChannel: state.calculate.selectChannelList && state.calculate.selectChannelList[tab],
    taxFilter: state.ui.selectbox?.["calculate_tax_filter"],
    payFilter: state.ui.selectbox?.["calculate_pay_filter"],
    approvalFilter: state.ui.selectbox?.["calculate_approval_filter"],
  }));

  const { loadList } = useContentsCalculateList(tab);

  useEffect(() => {
    loadList();
    return () => {
      dispatch(rdxResetCalculateGridData(subTab));
    };
  }, [subTab, selectChannel, year, searchMonth, taxFilter, payFilter, approvalFilter]);

  return { loadList };
};

/**
 * 예상수익 페이지 Init
 * @returns list 조회한 값
 */
export const useExpectPageInit = (tab: string) => {
  const dispatch = useDispatch();

  const { list, pageInfo, date } = useSelectorEq((state: IState) => ({
    list: state.calculate.expect.list,
    pageInfo: state.calculate.expect.pageInfo,
    date: state.ui.inputText?.[`${tab}_Calendar_Date`],
  }));
  const { loadList } = useExpectList();

  useEffect(() => {
    if (date) {
      loadList();
    }

    return () => {
      dispatch(rdxResetExpectListData());
    };
  }, [date]);

  return { list, pageInfo, loadList };
};

/**
 * 정산금 페이지 Init
 * @returns list 조회한 값
 */
export const useSettlementDataLoad = (tab: string, subTab: string) => {
  const dispatch = useDispatch();
  const { year, searchMonth } = useSelectorSearchDate(tab, subTab);

  const { loadList } = useSettlementList();

  useEffect(() => {
    if (year) {
      loadList();
    }

    return () => {
      dispatch(rdxResetSettlementListData());
    };
  }, [year, searchMonth]);

  return { loadList };
};

export const useSettlementPageInit = () => {
  const { excelViewList, pageInfo } = useSelectorEq((state: IState) => ({
    excelViewList: state.calculate.settlement.excelViewList,
    pageInfo: state.calculate.settlement.pageInfo,
  }));
  return { excelViewList, pageInfo };
};

/**
 * 정산 리스트에서 저장버튼 눌렀는지 체크 (정산발행하기 버튼은 저장하기 이후에 클릭 가능)
 * @param subTab 서브탭
 * @returns
 */
export const useSaveFlag = (subTab: string) => {
  const { saveFlag } = useSelectorEq((state: IState) => ({
    saveFlag: state.calculate.contents[subTab]?.saveFlag,
  }));

  return { saveFlag };
};

/**
 * 요율 100% 넘어가는지 체크
 * @param subTab 서브탭
 * @returns check: 요율 100넘어가는 곳이 있는지, channel_name : 요율 100 넘어간 채널
 */
export const useRatioNumberCheck = (subTab: string) => {
  const { list } = useSelectorEq((state: IState) => ({
    list: state.calculate.contents[subTab]?.viewList ? state.calculate.contents[subTab].viewList : [],
  }));

  const detailList = list.map(i => i.list).flat();

  const ratio_check = detailList.filter(i => i.ratio && i.ratio > 100);
  const channel_name = ratio_check.map(i => i.name).join(",");
  const check = ratio_check.length > 0 ? false : true;

  return { check, channel_name };
};

/**
 * @function useExcelLoadBtnAction 엑셀 불러오기
 * @returns dataSave: 엑셀 불러오기, getDownloadExcel: 엑셀 다운로드
 */
export function useExcelLoadBtnAction() {
  const { apiResult } = useApi();
  const { on, off } = useLoading();
  const iconPopup = useIConPopup();
  const alert = useAlert();

  // 엑셀 불러오기
  const dataSave = async () => {
    try {
      on();
      const res = await apiResult(apiPMsnExcelSave);

      if (res && res.code === 200) {
        off();
        iconPopup(
          "alert",
          {
            iconColor: "00c42d",
            iconType: "Check",
            title: "엑셀 정보가 저장되었습니다.",
          },
          undefined,
          v => {
            if (v && v > 0) {
              location.reload();
            }
          },
        );
      } else {
        off();
        alert("일시적인 오류가 발생했습니다.<br/>관리자에게 문의 부탁드립니다.");
      }
    } catch (err) {
      off();
      throw new Error("API Error: apiPMsnExcelSave");
    }
  };

  // 엑셀 다운로드
  const getDownloadExcel = async () => {
    on();
    await apiResult(apiGdownloadExcel);
    off();
  };

  return { dataSave, getDownloadExcel };
}

export const useFileReadHook = () => {
  const dispatch = useDispatch();

  const uploadFile = async (filedata: any) => {
    const values = filedata.data.slice(1);

    if (values.length <= 1 && values[0][0] === "") {
      alert("불러올 데이터가 없습니다.");
      return;
    }

    //타입이 음원/영상인 경우
    const viewGridData: {
      channel_id: string;
      name: string;
      page_count: number;
      settlement: number;
    }[] = [];

    values.map((array: Array<string>) => {
      if (array.length <= 1 && array.toString().length < 1) {
        return false;
      }
      const detail = {
        channel_id: array[0],
        name: array[2],
        page_count: Number(array[3]),
        settlement: Number(array[4].trim().length > 0 ? array[4] : "0"),
      };

      viewGridData.push(detail);

      return detail;
    });

    dispatch(rdxSetSettlementExcelViewList(viewGridData));
  };

  useEffect(() => {
    return () => {
      dispatch(rdxResetSettlementExcelViewList());
    };
  }, []);

  return uploadFile;
};

/**
 * @function useDataBtnClickAction 정산 리스트 버튼 액션 모음
 * @returns {dataSave} 데이터 저장 함수
 */
export function useDataBtnClickAction() {
  const { apiResult } = useApi();
  const { on, off } = useLoading();
  const iconPopup = useIConPopup();
  const alert = useAlert();

  /**
   * @function dataSave 데이터 저장 버튼 클릭 시 실행 함수
   * @param type - save: 뷰 리스트 저장, publish: 정산 발행내역 저장, excel: 음원영상 탭에서 정산내역발행
   */
  const dataSave = async (type: string) => {
    try {
      on();

      const res =
        type === "save"
          ? await apiResult(apiPcalculateViewListSave)
          : type === "publish"
          ? await apiResult(apiPcalculatePublish)
          : await apiResult(apiPpublishMediaExcel);
      if (res && res.code === 200) {
        off();
        iconPopup("alert", {
          iconColor: "00c42d",
          iconType: "Check",
          title: type === "save" ? "저장되었습니다." : "정산내역이 발행되었습니다.",
        });

        // iconPopup(
        //   "alert",
        //   {
        //     iconColor: "ddff3d",
        //     iconType: "Check",
        //     title: "알림톡이 발송되었습니다.",
        //   },
        //   undefined,
        //   // v => {
        //   //   v && v > 0 && loadList(false);
        //   // },
        // );
      } else {
        off();
        alert("일시적인 오류가 발생했습니다.\n관리자에게 문의 부탁드립니다.");
      }
    } catch (err) {
      off();
      throw new Error("API Error: useDataBtnClickAction Hook 참고");
    }
  };
  return { dataSave };
}

export const useTaxSelectChange = (subTab: string) => {
  const dispatch = useDispatch();
  const change = async (type: string, id: number, val?: string | number) => {
    if (val) {
      dispatch(rdxSetSaveFlag(subTab));
      dispatch(rdxSetCalculateTaxRowUpdate({ subTab: subTab, type: type, id: id, value: String(val) }));
    }
  };

  const filter = async (type: "tax" | "pay", val?: string | number) => {
    // console.log("filter function");
    // console.log(val);
    // dispatch(rdxSetViewListFilter({ subTab: subTab, type: type, value: val ? String(val) : undefined }));
  };

  return { change, filter };
};
/**
 * 음원정산 데이터 아닐 경우 엑셀 다운로드
 * @param tab
 * @param subTab
 * @returns
 */
export const useCalculateExcelDown = (tab: string, subTab: string) => {
  const { viewList, total } = useSelectorEq((state: IState) => ({
    viewList: state.calculate.contents[subTab].viewList,
    total: state.calculate.contents[subTab].total,
  }));

  const exportToExcel = () => {
    // 데이터 배열 생성
    const data = [];

    // 테이블 헤더
    const headers = [
      "사업자명",
      "채널명",
      "조회수",
      tab === ActiveType.CON ? "예상 매출" : "발생 매출",
      ...(tab !== ActiveType.CON
        ? [`${tab === ActiveType.CP ? "CP" : "PN"} 요율(%)`, `${tab === ActiveType.CP ? "CP" : "PN"} 정산금`]
        : []),
      tab === ActiveType.CON ? "정산금" : "추가 정산금",
      "최종 합계",
      ...(tab !== ActiveType.CON ? ["단가"] : []),
      "세금계산서",
      "지급",
      "지급일자",
      "메모",
    ];

    data.push(headers);

    // 테이블 데이터
    viewList.forEach(item => {
      item.list.forEach((i, idx) => {
        const taxStatus =
          item.tax_status === "ISSUE" ? "발행" : item.tax_status === "NOT_ISSUE" ? "미발행" : "금액상이";
        const payStatus =
          item.pay_status === "COMPLETED" ? "지급완료" : item.pay_status === "IN_COMPLETED" ? "미지급" : "금액상이";

        const row = [
          item.business_name || "-",
          i.name,
          i.page_view,
          i.sales,
          ...(tab !== ActiveType.CON ? [i.ratio, i.settlement] : []),
          i.add_settlement,
          i.calculate,
          ...(tab !== ActiveType.CON ? [i.unit_price] : []),
          taxStatus,
          payStatus,
          item.pay_date,
          item.message,
        ];
        data.push(row);
      });
    });

    // 합계 데이터 추가
    const totalRow = [
      "합계",
      "",
      total ? total.page_view : 0,
      total ? total.sales : 0,
      ...(tab !== ActiveType.CON ? ["", total ? total.cp_settlement : 0] : []),
      total ? total.add_settlement : 0,
      total ? total.calculate : 0,
      ...(tab !== ActiveType.CON ? [""] : []),
      "",
      "",
      "",
      "",
    ];
    data.push(totalRow);

    // 워크시트 생성
    const ws = XLSX.utils.aoa_to_sheet(data);

    // 워크북 생성 및 워크시트 추가
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    // 엑셀 파일 생성 및 다운로드
    XLSX.writeFile(wb, `${tab}_${subTab}_data.xlsx`);
  };
  return { exportToExcel };
};

// // 세금계산서, 지급여부 등 필터
// export const useListFilter = () => {
//   const { tax, pay } = useSelectorEq((state: IState) => ({
//     tax: state.ui.selectbox?.[`calculate_tax_filter`],
//     pay: state.ui.selectbox?.[`calculate_pay_filter`],
//   }));

//   console.log(tax);
//   console.log(pay);
// };
